/**
 * MerakiWorkflowPicker — Phase 3 sub-phase 1.
 *
 * The first React port of an `app_meraki/*` template. Lets the operator
 * browse runnable workflows, filter them, and click into one to start a
 * push (the runner page comes in sub-phase 2).
 *
 * Today the data comes from the workflows store, which falls back to a
 * local fixture if `/api/meraki/workflows/` is not yet wired. The UI shape
 * is the same against either source — when the Django endpoint ships, no
 * UI changes are needed.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Search,
  Sparkles,
  AlertTriangle,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardHeader,
  CardTitle,
  CardSub,
  CardContent,
} from "@/components/ui/card";
import { CardGridSkeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MerakiSubNav } from "@/components/app/meraki-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { useMerakiWorkflows } from "@/stores/meraki-workflows";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

/**
 * Bucket a workflow's recommended score into a color so it reads at a glance.
 * Thresholds match the legacy provisioning UI's score badges.
 */
function scoreVariant(score: number): {
  variant: "ok" | "warn" | "neutral";
  label: string;
} {
  if (score >= 80) return { variant: "ok", label: "highly recommended" };
  if (score >= 60) return { variant: "warn", label: "recommended" };
  return { variant: "neutral", label: "optional" };
}

type SortKey = "score" | "name";

export function MerakiWorkflowPicker() {
  const animatedRootRef = useUiAutoAnimate();

  const { data, status, error, load } = useMerakiWorkflows();

  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortKey>("score");

  // Fetch on mount. Store guards against duplicate concurrent loads.
  useEffect(() => {
    void load();
  }, [load]);

  // Filter + sort. Memoized so re-renders from typing don't re-walk the array
  // unnecessarily for unchanged inputs.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? data.filter(
          (w) =>
            w.name.toLowerCase().includes(q) ||
            w.description.toLowerCase().includes(q) ||
            w.slug.toLowerCase().includes(q),
        )
      : data;
    return [...filtered].sort((a, b) => {
      if (sortBy === "score") return b.recommendedScore - a.recommendedScore;
      return a.name.localeCompare(b.name);
    });
  }, [data, query, sortBy]);

  const isLoading = status === "loading" && data.length === 0;
  const isFixtureFallback = status === "error";

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      {/* Crumb */}
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/meraki" className="text-fg-dim hover:text-fg">
          Meraki
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Provisioning</span>
      </div>

      {/* Vendor sub-nav */}
      <MerakiSubNav />

      <NoVendorKeysBanner vendor="meraki" />

      {/* Hero */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Provisioning workflows
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Choose an automation to run against the selected sites.
            Recommendations are scored against current site posture and recent
            activity.
          </p>
        </div>
        <Button variant="ghost">
          <Sparkles size={14} />
          New custom workflow
        </Button>
      </div>

      {/* Fallback warning — shown when the API failed and we're on fixtures. */}
      {isFixtureFallback ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search
            size={14}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name, slug, or description…"
            className="pl-9"
          />
        </div>
        <Tabs value={sortBy} onValueChange={(v) => setSortBy(v as SortKey)}>
          <TabsList>
            <TabsTrigger value="score">Score</TabsTrigger>
            <TabsTrigger value="name">A → Z</TabsTrigger>
          </TabsList>
        </Tabs>
        <span className="ml-auto font-mono text-xs text-fg-mute">
          {visible.length}/{data.length} shown
        </span>
      </div>

      {/* List */}
      {isLoading ? (
        <SkeletonList />
      ) : visible.length === 0 ? (
        <EmptyState query={query} />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {visible.map((w) => {
            const { variant, label } = scoreVariant(w.recommendedScore);
            return (
              <Card key={w.slug}>
                <CardHeader>
                  <div className="min-w-0">
                    <CardTitle className="truncate">{w.name}</CardTitle>
                    <CardSub className="font-mono">{w.slug}</CardSub>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant={variant} size="md">
                      {w.recommendedScore}
                    </Badge>
                    <span className="text-[10px] uppercase tracking-[0.1em] text-fg-mute">
                      {label}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <p className="m-0 text-sm leading-relaxed text-fg-dim">
                    {w.description}
                  </p>

                  {/* Score-reason bullets — explain WHY the score is what it is. */}
                  {w.scoreReasons.length > 0 ? (
                    <ul className="flex flex-col gap-1.5 m-0 p-0 list-none">
                      {w.scoreReasons.map((r) => (
                        <li
                          key={r}
                          className={cn(
                            "flex items-start gap-2 text-xs text-fg-mute",
                            "before:content-['·'] before:text-violet before:mt-[-2px]",
                          )}
                        >
                          <Info
                            size={11}
                            className="mt-0.5 shrink-0 text-violet"
                          />
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <div className="flex justify-end">
                    <Button asChild size="sm">
                      <Link to={`/meraki/provisioning/run/${w.slug}`}>
                        Run workflow
                        <ArrowRight size={13} />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Skeleton — shown only on the very first load (no cached data yet). */
function SkeletonList() {
  return <CardGridSkeleton cards={4} />;
}

/** Empty / no-match state — give the user something to do, not a dead end. */
function EmptyState({ query }: { query: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-aurora-soft text-violet">
          <Search size={20} />
        </div>
        <div>
          <CardTitle>No workflows match "{query}"</CardTitle>
          <CardSub className="mt-1">
            Try a different search or clear the filter.
          </CardSub>
        </div>
      </CardContent>
    </Card>
  );
}
